// src/pages/FanGroupPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import FanGroupForm from '../components/FanGroupForm';
import { AnimatePresence } from 'framer-motion';
import Navbar from '../components/NavBar';
import citiesData from '../data/CitiesData';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const API_BASE_URL = 'https://klima-backend-ggo2.onrender.com';

function createNewUnit(id) {
  return {
    id,
    type: 'fan',
    unitName: `FAN-${id.toString().padStart(2, '0')}`,
    deviceType: '',
    subType: '',
    aspFlow: '',
    aspPressure: '',
    exproofClass: '',
    quantity: 1,
  };
}

function mergeUnitData(defaultUnit, dbUnit) {
  return {
    ...defaultUnit,
    ...dbUnit,
    id: dbUnit.id ?? defaultUnit.id,
    unitName: dbUnit.unitName ?? defaultUnit.unitName,
  };
}


const FanGroupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pdfRef = useRef();
  const queryParams = new URLSearchParams(location.search);
  const editId = queryParams.get('edit');

  const [username, setUsername] = useState('Ziyaretçi');
  const [projectName, setProjectName] = useState('');
  const [projectLocation, setProjectLocation] = useState('');
  const [fanNotes, setFanNotes] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [climateData, setClimateData] = useState({
    altitude: '',
    winterDB: '',
    summerDB: '',
    summerWB: '',
  });
  const [units, setUnits] = useState([createNewUnit(1)]);
  const [allUnits, setAllUnits] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUsername(payload.username || 'Ziyaretçi');
      } catch {
        setUsername('Ziyaretçi');
      }
    }
  }, []);

  useEffect(() => {
    if (editId) {
      fetch(`${API_BASE_URL}/api/projects/${editId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })
        .then(res => res.json())
        .then(data => {
          setProjectName(data.projectName || '');
          setProjectLocation(data.location || '');
          setClimateData({
            altitude: data.altitude || '',
            winterDB: data.winterDryTemp || '',
            summerDB: data.summerDryTemp || '',
            summerWB: data.summerWetTemp || '',
          });

          setUploadedFiles(data.uploadedFiles || []);
          setFanNotes(data.fanNotes || '');
          setAllUnits(data.units || []);
          const fanUnits = data.units?.filter(u => u.type === 'fan') || [];
          setUnits(
  fanUnits.map((u, i) => {
    const defaults = createNewUnit(i + 1);
    return mergeUnitData(defaults, u);
  })
);

        })
        .catch(err => console.error('❌ Proje verisi alınamadı:', err));
    }
  }, [location.search]);

  const handleUnitChange = (id, updated) => {
    setUnits(units.map((u) => (u.id === id ? updated : u)));
  };

  const handleCopy = (unit) => {
    const newId = units.length + 1;
    setUnits([...units, { ...unit, id: newId, unitName: `FAN-${newId.toString().padStart(2, '0')}` }]);
  };

  const handleDelete = (id) => {
    if (window.confirm('Bu fan grubunu silmek istiyor musunuz?')) {
      setUnits(units.filter((u) => u.id !== id));
    }
  };

  const handleAdd = () => {
    const newId = units.length + 1;
    setUnits([...units, createNewUnit(newId)]);
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    const formData = new FormData();
    for (let file of files) {
      if (file.size > 5 * 1024 * 1024) {
        alert(`${file.name} dosyası 5 MB'tan büyük.`);
        return;
      }
      formData.append('files', file);
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/upload`, {
        method: 'POST',
        body: formData,
      });
      const result = await res.json();
      setUploadedFiles(result.uploadedFiles);
    } catch {
      alert('Yükleme başarısız.');
    }
  };

  const generatePDF = () => {
    const input = pdfRef.current;
    html2canvas(input, { scale: 2 }).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
      heightLeft -= pdf.internal.pageSize.getHeight();

      while (heightLeft > 0) {
        position -= pdf.internal.pageSize.getHeight();
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
        heightLeft -= pdf.internal.pageSize.getHeight();
      }

      pdf.save('fan_grubu.pdf');
    });
  };

  const handleSaveProject = async () => {
    if (username === 'Ziyaretçi') {
      alert('Projeyi Kaydedebilmek İçin Lütfen Giriş Yapınız');
      return;
    }

    const updatedUnits = [
      ...allUnits.filter(u => u.type !== 'fan'),
     ...units.map((u, i) => ({
  ...createNewUnit(i + 1), // varsayılanları garanti altına al
  ...u,                    // sonra kullanıcıdan geleni üzerine yaz
  type: 'fan',
  id: u.id ?? i + 1,
  unitName: u.unitName ?? `FAN-${(i + 1).toString().padStart(2, '0')}`,
})),
    ];

    const data = {
      projectName,
      location: projectLocation,
      fanNotes, // ✅ bunu ekle
      altitude: Number(climateData.altitude),
      winterDryTemp: Number(climateData.winterDB),
      summerDryTemp: Number(climateData.summerDB),
      summerWetTemp: Number(climateData.summerWB),
      units: updatedUnits,
      uploadedFiles,
      updatedAt: new Date().toISOString(),
    };

    const token = localStorage.getItem('token');
    const method = editId ? 'PUT' : 'POST';
    const endpoint = editId
      ? `${API_BASE_URL}/api/projects/${editId}`
      : `${API_BASE_URL}/api/projects`;

    try {
      const res = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Proje kaydedilemedi');
      alert('✅ Proje kaydedildi.');
      navigate('/panel');
    } catch (err) {
      alert('❌ Kayıt hatası: ' + err.message);
    }
  };

  return (
    <>
      <Navbar username={username} />

      <div ref={pdfRef} className="pdf-section" style={{ maxWidth: 1400, margin: 'auto', padding: 20 }}>

         <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h1>Fan Grubu Seçimi</h1>
        </div>

  <div className="form-row align-center" style={{ position: 'relative' }}>
    <label>Proje İsmi:</label>
    <input
      type="text"
      value={projectName}
      onChange={(e) => setProjectName(e.target.value)}
      readOnly={username === 'Ziyaretçi'}
      placeholder={username === 'Ziyaretçi' ? '' : 'Proje ismini giriniz'}
      style={{
        backgroundColor: username === 'Ziyaretçi' ? '#f0f0f0' : '#fff',
        border: '1px solid #ccc',
        borderRadius: 6,
        padding: '8px 10px',
        fontSize: '16px',
        height: 38,
        lineHeight: '1.2',
        width: '200px',
        marginRight: 12,
        boxSizing: 'border-box',
        cursor: username === 'Ziyaretçi' ? 'not-allowed' : 'auto',
      }}
      title={username === 'Ziyaretçi' ? 'Değer girebilmek için lütfen giriş yapınız.' : ''}
    />
  </div>

  <div className="form-row align-center" style={{ position: 'relative', marginBottom: 20 }}>
    <label>Proje Konumu:</label>
    <input
      type="text"
      value={projectLocation}
      disabled
      style={{
        backgroundColor: '#f0f0f0',
        border: '1px solid #ccc',
        borderRadius: 6,
        padding: '8px 10px',
        fontSize: '16px',
        height: 38,
        lineHeight: '1.2',
        width: '200px',
        marginRight: 12,
        boxSizing: 'border-box',
        cursor: username === 'Ziyaretçi' ? 'not-allowed' : 'auto',
      }}
      title={username === 'Ziyaretçi' ? 'Değer girebilmek için lütfen giriş yapınız.' : ''}
    />
    <span className="climate-info">
      Rakım: {climateData.altitude} m | Kış KT: {climateData.winterDB} °C | Yaz KT: {climateData.summerDB} °C | Yaz YT: {climateData.summerWB} °C
    </span>
  </div>

  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
    <AnimatePresence>
      {units.map((unit) => (
        <FanGroupForm
          key={unit.id}
          unit={unit}
          onChange={(updated) => handleUnitChange(unit.id, updated)}
          onCopy={() => handleCopy(unit)}
          onDelete={() => handleDelete(unit.id)}
        />
      ))}
    </AnimatePresence>
  </div>

  <div style={{ marginTop: 30, marginBottom: 30, textAlign: 'center' }}>
    <button
      onClick={handleAdd}
      style={{
        backgroundColor: '#FFA500',
        color: 'white',
        padding: '10px 20px',
        fontWeight: 'bold',
        borderRadius: 6,
        border: 'none',
        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
        cursor: 'pointer',
      }}
    >
      ➕ Yeni Fan Grubu Ekle
    </button>
    </div> {/* pdf-section kapanışı */}

    <div style={{ marginTop: 20 }}>
  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 6 }}>Notlar:</label>
  <textarea
    value={fanNotes}
onChange={(e) => setFanNotes(e.target.value)}
    placeholder="Varsa ekstra notlarınızı buraya yazabilirsiniz..."
    style={{
      width: '100%',
      minHeight: 100,
      padding: '10px',
      fontSize: '15px',
      border: '1px solid #ccc',
      borderRadius: 6,
      resize: 'vertical',
    }}
  />
</div>


    <div className="btn-group" style={{ display: 'flex', gap: 10, marginTop: 25, justifyContent: 'center' }}>
      <button
        className="btn-save"
        style={{ backgroundColor: '#1e88e5', color: 'white', padding: '10px 16px', borderRadius: 6 }}
        onClick={handleSaveProject}
      >
        💾 Projeyi Kaydet
      </button>
      <button
        className="btn-pdf"
        style={{ backgroundColor: '#e53935', color: 'white', padding: '10px 16px', borderRadius: 6 }}
        onClick={generatePDF}
      >
        📄 PDF Oluştur
      </button>
      <button
        onClick={() => navigate('/panel')}
        style={{ backgroundColor: '#9e9e9e', color: 'white', padding: '10px 16px', borderRadius: 6 }}
      >
        Vazgeç
      </button>
    </div>
  </div> {/* pdf-section kapanışı */}
    </>
  ); // return parantezi

}; // FanGroupPage fonksiyonu kapanışı

export default FanGroupPage;
